import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MovdMarketingHero from '../design-system/MovdMarketingHero.jsx';
import MovdButton from '../design-system/MovdButton.jsx';
import MovdNotice from '../design-system/MovdNotice.jsx';
import { movdTokens } from '../design-system/movdTokens.js';

const cardStyle = { padding: 24, textAlign: 'center', borderRadius: 12, boxShadow: movdTokens.shadow.raised, background: '#fff' };

const values = [
  { icon: '\uD83D\uDEE1', title: 'Trust & Safety', description: 'Every mover is thoroughly vetted, licensed, and insured for your peace of mind.' },
  { icon: '\u26A1', title: 'Efficiency', description: 'Get instant quotes and book your move in minutes, not hours.' },
  { icon: '\uD83C\uDFA7', title: 'Support', description: '24/7 customer support to guide you through every step of your move.' },
  { icon: '\u2B50', title: 'Quality Focus', description: 'We aim to list movers who take pride in their work. Ratings and reviews will be verified as our community grows.' },
];

const whyChoose = [
  'Instant quotes from movers in your area',
  'Transparent pricing with no hidden fees',
  'Digital inventory to document your items',
  'Secure online booking and payment',
  'Email confirmation and move reminders',
  'Support if anything goes wrong',
];

export default function AboutPage() {
  const [notice, setNotice] = useState(null);
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh' }}>
      <MovdMarketingHero eyebrow="Who we are" title="About Movd" subtitle="Canada's moving platform built for transparency — connecting you with movers who show up and do the job right." compact />

      <div style={{ maxWidth: 1120, margin: '0 auto', padding: 'clamp(24px, 5vw, 48px) 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 32, alignItems: 'center', marginBottom: 48 }}>
          <div>
            <p style={{ margin: 0, color: movdTokens.color.action.primary, letterSpacing: '.2em', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Our story</p>
            <h2 style={{ margin: '8px 0 16px', fontWeight: 800, fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>Building the moving platform Canada deserves</h2>
            <p style={{ margin: '0 0 16px', fontSize: 16, lineHeight: 1.6, color: movdTokens.color.content.secondary }}>Moving can be one of life&apos;s most stressful experiences. We founded Movd to change that, creating a platform that connects Canadians with reliable, vetted moving professionals across the country.</p>
            <p style={{ margin: 0, fontSize: 16, lineHeight: 1.6, color: movdTokens.color.content.secondary }}>We&apos;re building something new. Movd is a platform that connects Canadians with moving professionals — with transparent pricing, digital inventory, and a booking flow that actually makes sense.</p>
          </div>
          <div style={{ borderRadius: 12, overflow: 'hidden', background: '#e2e8f0', minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src="/img/about-story.jpg" alt="Movd team at work" style={{ width: '100%', height: 'auto', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />
          </div>
        </div>

        <p style={{ margin: 0, color: movdTokens.color.action.primary, letterSpacing: '.2em', fontSize: 11, fontWeight: 700, textAlign: 'center', textTransform: 'uppercase' }}>What we stand for</p>
        <h2 style={{ margin: '8px 0 32px', fontWeight: 800, fontSize: 'clamp(1.5rem, 3vw, 2rem)', textAlign: 'center' }}>Our values</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24, marginBottom: 48 }}>
          {values.map(value => (
            <div key={value.title} style={cardStyle}>
              <div style={{ fontSize: 36, marginBottom: 12 }} aria-hidden="true">{value.icon}</div>
              <h3 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: 18 }}>{value.title}</h3>
              <p style={{ margin: 0, color: movdTokens.color.content.secondary, fontSize: 14, lineHeight: 1.5 }}>{value.description}</p>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 32, alignItems: 'center' }}>
          <div style={{ borderRadius: 12, overflow: 'hidden', background: '#e2e8f0', minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src="/img/about-choose.jpg" alt="Happy customers with Movd" style={{ width: '100%', height: 'auto', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />
          </div>
          <div>
            <h2 style={{ margin: '0 0 16px', fontWeight: 800, fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>Why choose Movd?</h2>
            <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
              {whyChoose.map(feature => (
                <div key={feature} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span aria-hidden="true" style={{ color: movdTokens.color.state.success }}>✓</span>
                  <span style={{ color: movdTokens.color.content.secondary, fontSize: 16 }}>{feature}</span>
                </div>
              ))}
            </div>
            <MovdButton onClick={() => navigate('/')}>Start your move today</MovdButton>
          </div>
        </div>

        <div style={{ marginTop: 48, padding: 'clamp(24px, 5vw, 40px)', textAlign: 'center', borderRadius: 12, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', border: '1px solid #e2e8f0' }}>
          <h2 style={{ margin: '0 0 8px', fontWeight: 800, fontSize: 22 }}>Ready to experience the difference?</h2>
          <p style={{ margin: '0 0 20px', color: movdTokens.color.content.secondary, maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>Get instant quotes from verified movers and start planning your move today.</p>
          <MovdButton onClick={() => { setNotice({ severity: 'success', text: 'Redirecting you to get a quote…' }); navigate('/'); }}>Get your free quote</MovdButton>
        </div>
      </div>

      {notice && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1500, minWidth: 280 }}><MovdNotice tone={notice.severity}>{notice.text}</MovdNotice></div>}
    </div>
  );
}
